import { useEffect, useState, useCallback } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Trash2, Archive } from "lucide-react";

const HORSE_PLACEHOLDER = "https://images.pexels.com/photos/32076745/pexels-photo-32076745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const AdminSubmissions = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/submissions", { params: { status: filter || undefined } });
      setItems(data || []);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [filter, load]);

  const setStatus = async (id, status) => {
    try {
      const fd = new FormData();
      fd.append("status", status);
      await api.post(`/submissions/${id}/mark`, fd);
      toast.success("Шинэчлэгдлээ");
      load();
    } catch { toast.error("Алдаа гарлаа"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Устгах уу?")) return;
    try {
      await api.delete(`/submissions/${id}`);
      toast.success("Устгагдлаа");
      load();
    } catch { toast.error("Алдаа"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-[#1B3B5A] hover:text-[#B8860B] mb-6"><ArrowLeft size={18} /> Буцах</Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#B8860B] font-bold mb-1">Ирсэн хүсэлтүүд</div>
          <h1 className="text-3xl font-bold text-[#1B3B5A]">Уяачдаас ирсэн мэдээлэл</h1>
        </div>
        <div className="flex items-center gap-2">
          {[["pending", "Шинэ"], ["reviewed", "Үзсэн"], ["archived", "Архивлагдсан"], ["", "Бүгд"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-xs font-bold rounded border ${filter === k ? "bg-[#1B3B5A] text-white border-[#1B3B5A]" : "border-[#D6CFC4] text-[#2C241B] hover:border-[#B8860B]"}`}
              data-testid={`sub-filter-${k || "all"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C4F42]">Ачааллаж байна...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-[#5C4F42]">Хүсэлт алга.</div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="bg-white border border-[#D6CFC4] rounded-lg p-4" data-testid={`submission-${s.id}`}>
              <div className="flex items-start gap-4">
                {s.photo_path ? (
                  <img src={fileUrl(s.photo_path)} alt="horse" className="w-24 h-24 object-cover rounded border border-[#D6CFC4]" />
                ) : (
                  <img src={HORSE_PLACEHOLDER} alt="placeholder" className="w-24 h-24 object-cover rounded border border-[#D6CFC4] opacity-60" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded font-bold ${s.status === "pending" ? "bg-[#B8860B] text-white" : s.status === "reviewed" ? "bg-[#059669] text-white" : "bg-[#5C4F42]/20 text-[#2C241B]"}`}>
                      {s.status}
                    </span>
                    <span className="text-xs text-[#5C4F42]">{new Date(s.created_at).toLocaleString("mn-MN")}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#1B3B5A]">{s.horse_name}</h3>
                  <div className="text-xs text-[#5C4F42] mt-1">
                    <span className="font-bold">Илгээсэн:</span> {s.submitter_name}{s.submitter_phone ? ` · ☏ ${s.submitter_phone}` : ""}
                  </div>
                  {s.sale_price && (
                    <div className="inline-flex items-center gap-2 mt-2 bg-[#B8860B] text-white px-3 py-1 rounded text-sm font-bold" data-testid={`sub-price-${s.id}`}>
                      Зарах үнэ: {s.sale_price}
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-[#2C241B]">
                    {s.birth_year && <div><b>Төрсөн:</b> {s.birth_year}</div>}
                    {s.color && <div><b>Зүс:</b> {s.color}</div>}
                    {s.gender && <div><b>Хүйс:</b> {s.gender}</div>}
                    {s.microchip && <div><b>Микрочип:</b> {s.microchip}</div>}
                    {s.brand_stamp && <div><b>Тамга:</b> {s.brand_stamp}</div>}
                    {s.registration_number && <div><b>Бүртгэл:</b> {s.registration_number}</div>}
                    {s.country && <div><b>Улс:</b> {s.country}</div>}
                    {s.owner_name && <div><b>Эзэмшигч:</b> {s.owner_name}</div>}
                    {s.breeder_farm && <div><b>Үржүүлэгч:</b> {s.breeder_farm}</div>}
                    {s.father_name && <div><b>Эцэг:</b> {s.father_name}</div>}
                    {s.mother_name && <div><b>Эх:</b> {s.mother_name}</div>}
                  </div>
                  {s.description && <div className="text-xs text-[#2C241B] mt-2 whitespace-pre-line">{s.description}</div>}
                </div>
                <div className="flex flex-col gap-2">
                  {s.status !== "reviewed" && (
                    <button onClick={() => setStatus(s.id, "reviewed")} className="inline-flex items-center gap-1 bg-[#059669] text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-[#047857]" data-testid={`sub-review-${s.id}`}>
                      <Check size={12} /> Үзсэн
                    </button>
                  )}
                  {s.status !== "archived" && (
                    <button onClick={() => setStatus(s.id, "archived")} className="inline-flex items-center gap-1 bg-[#5C4F42]/20 text-[#2C241B] px-3 py-1.5 rounded text-xs font-bold hover:bg-[#5C4F42]/30" data-testid={`sub-archive-${s.id}`}>
                      <Archive size={12} /> Архивлах
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} className="inline-flex items-center gap-1 bg-[#8B2500]/10 text-[#8B2500] px-3 py-1.5 rounded text-xs font-bold hover:bg-[#8B2500]/20" data-testid={`sub-delete-${s.id}`}>
                    <Trash2 size={12} /> Устгах
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissions;
